import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type ReactNode
} from "react";
import type { GallerySnapshot, ResearchReportDetail } from "@roboscientist/contracts";
import { EmptyState, OverflowReveal, Panel, StatusPill } from "@roboscientist/ui";
import {
  ArrowLeft,
  Bot,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Clock3,
  Download,
  FileArchive,
  FileCheck2,
  FileText,
  FlaskConical,
  FolderArchive,
  Eye,
  MessageSquareText,
  Moon,
  Network,
  PackageOpen,
  Pencil,
  Save,
  ShieldCheck,
  Sun,
  Trash2,
  Upload,
  X
} from "lucide-react";
import { pickTelemetryRun } from "../../web/src/components/TelemetryRunSelector.js";
import { ResearchReportReader } from "../../web/src/components/ResearchReportReader.js";
import { ResearchRequirementView } from "../../web/src/components/ResearchRequirementView.js";
import { formatReportTimestamp, reportKindLabel } from "../../web/src/components/reportPresentation.js";
import { useCockpitStore } from "../../web/src/store.js";
import { useThemeController } from "../../web/src/useThemeController.js";
import {
  deleteGallerySnapshot,
  gallerySnapshotAsset,
  listGallerySnapshots,
  storeGallerySnapshot,
  updateGallerySnapshotMessage
} from "./storage.js";
import { enhanceNavigationGlass } from "./quietGlass.js";
import { GalleryHome } from "./GalleryHome.js";
import { englishGalleryText } from "./galleryEnglish.js";
import { galleryDisplaySnapshots } from "./galleryDisplay.js";
import { GalleryDeployView } from "./GalleryDeployView.js";
import { GalleryRunsView } from "./GalleryRunsView.js";
import { GalleryEvolutionReplay } from "./GalleryEvolutionReplay.js";
import { MAX_GALLERY_MESSAGE_LENGTH } from "./storageMetadata.js";
import { listPublishedStudies, loadPublishedSnapshot, publicReadOnly, studyFromSnapshot, type GalleryStudy } from "./publicGallery.js";

type GalleryView = "requirement" | "evolution" | "runs" | "reports" | "paper";
type GalleryRoute =
  | { page: "gallery" }
  | { page: "manage" }
  | { page: "snapshot"; snapshotId: string };

const parseGalleryRoute = (): GalleryRoute => {
  const value = window.location.hash.replace(/^#\/?/, "");
  if (value === "manage" && !publicReadOnly) return { page: "manage" };
  if (value.startsWith("snapshot/")) {
    try {
      const snapshotId = decodeURIComponent(value.slice("snapshot/".length));
      if (snapshotId) return { page: "snapshot", snapshotId };
    } catch {
      // Invalid hashes fall back to the Gallery index.
    }
  }
  return { page: "gallery" };
};

const galleryRouteHash = (route: GalleryRoute): string => route.page === "manage"
  ? "#/manage"
  : route.page === "snapshot"
    ? `#/snapshot/${encodeURIComponent(route.snapshotId)}`
    : "#/";

const gallerySocialLinks = [
  { label: "Bilibili", description: "Video channel", url: "https://space.bilibili.com/1658699089?spm_id_from=333.1007.0.0" },
  { label: "Xiaohongshu", description: "Social updates", url: "https://xhslink.cn/o/7y18YCRvyHN" },
  { label: "X", description: "Global updates", url: "https://x.com/SimateAI" },
  { label: "YouTube", description: "Video channel", url: "https://www.youtube.com/@simate_ai" }
] as const;

function GalleryPublicFooter() {
  return <footer className="gallery-public-footer">
    <div className="gallery-public-footer__brand">
      <a href="/home/" aria-label="Simate home">Simate</a>
      <span>SEE A MATE. BE A MATE. Simate.</span>
    </div>
    <nav className="gallery-public-social" aria-label="Simate social media">
      <span className="gallery-public-social__label">Follow Simate</span>
      <div className="gallery-public-social__links">
        <span className="gallery-public-social__link is-pending" aria-label="WeChat Official account">
          <strong>WeChat</strong><small>Official account</small>
        </span>
        {gallerySocialLinks.map((social) => <a
          className="gallery-public-social__link"
          href={social.url}
          target="_blank"
          rel="noopener noreferrer"
          key={social.label}
        >
          <strong>{social.label}</strong><small>{social.description}</small><span aria-hidden="true">↗</span>
        </a>)}
      </div>
    </nav>
    <div className="gallery-public-footer__meta"><span>© {new Date().getFullYear()} Simate</span><a href="#gallery-title">Back to top ↑</a></div>
  </footer>;
}

function GalleryImportDialog({
  open,
  file,
  message,
  importError,
  importing,
  onFileChange,
  onMessageChange,
  onClose,
  onImport
}: {
  open: boolean;
  file: File | null;
  message: string;
  importError: string | null;
  importing: boolean;
  onFileChange: (file: File | null) => void;
  onMessageChange: (message: string) => void;
  onClose: () => void;
  onImport: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !importing) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [importing, onClose, open]);
  if (!open) return null;

  return <div
    className="gallery-dialog-overlay"
    onMouseDown={(event) => {
      if (event.target === event.currentTarget && !importing) onClose();
    }}
  >
    <section
      className="gallery-import-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gallery-import-title"
      aria-describedby="gallery-import-description"
    >
      <header>
        <span className="gallery-import-dialog__mark"><Upload size={18} /></span>
        <div>
          <span className="kicker">SNAPSHOT STORAGE</span>
          <h2 id="gallery-import-title">Import new snapshot</h2>
          <p id="gallery-import-description">Choose an experiment package and optionally leave a message for this gallery.</p>
        </div>
        <button className="gallery-dialog-close" type="button" aria-label="Close import dialog" onClick={onClose} disabled={importing}><X size={17} /></button>
      </header>
      <form onSubmit={(event) => { event.preventDefault(); if (file && !importing) onImport(); }}>
        <div className="gallery-import-field">
          <label htmlFor="gallery-import-file">Snapshot package <span>Required</span></label>
          <input
            ref={fileInputRef}
            id="gallery-import-file"
            className="gallery-import-input"
            type="file"
            accept=".zip,.rsexperiment.zip,.json,.rsexperiment.json,application/zip,application/json"
            onChange={(event) => onFileChange(event.currentTarget.files?.[0] ?? null)}
          />
          <button autoFocus className={`gallery-file-picker ${file ? "has-file" : ""}`} type="button" onClick={() => fileInputRef.current?.click()} disabled={importing}>
            <FileArchive size={22} />
            <span><strong>{file?.name ?? "Choose a .rsexperiment.zip file"}</strong><small>{file ? `${(file.size / 1024 / 1024).toFixed(2)} MB · Click to replace` : "ZIP package · up to 512 MB"}</small></span>
          </button>
        </div>
        <div className="gallery-import-field">
          <label htmlFor="gallery-import-message">Message <span>Optional</span></label>
          <div className="gallery-message-input">
            <MessageSquareText size={16} />
            <textarea
              id="gallery-import-message"
              value={message}
              maxLength={MAX_GALLERY_MESSAGE_LENGTH}
              rows={4}
              placeholder="Add context or a reminder about this snapshot…"
              onChange={(event) => onMessageChange(event.currentTarget.value)}
              disabled={importing}
            />
          </div>
          <small className="gallery-character-count">{message.length} / {MAX_GALLERY_MESSAGE_LENGTH}</small>
        </div>
        {importError ? <div className="gallery-import-dialog__error" role="alert"><PackageOpen size={15} /><span>{importError}</span></div> : null}
        <footer>
          <button className="button" type="button" onClick={onClose} disabled={importing}>Cancel</button>
          <button className="button button--primary" type="submit" disabled={!file || importing}>
            <Upload size={15} /> {importing ? "Importing…" : "Import snapshot"}
          </button>
        </footer>
      </form>
    </section>
  </div>;
}

function GalleryManage({
  snapshots,
  messages,
  loading,
  importing,
  deletingSnapshotId,
  savingMessageSnapshotId,
  storagePath,
  onImport,
  onOpen,
  onDelete,
  onUpdateMessage
}: {
  snapshots: GallerySnapshot[];
  messages: Record<string, string>;
  loading: boolean;
  importing: boolean;
  deletingSnapshotId: string | null;
  savingMessageSnapshotId: string | null;
  storagePath: string | null;
  onImport: () => void;
  onOpen: (snapshotId: string) => void;
  onDelete: (snapshot: GallerySnapshot) => void;
  onUpdateMessage: (snapshotId: string, message: string) => Promise<boolean>;
}) {
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [messageDraft, setMessageDraft] = useState("");
  useEffect(() => {
    if (editingMessageId && !snapshots.some((snapshot) => snapshot.snapshot.id === editingMessageId)) {
      setEditingMessageId(null);
      setMessageDraft("");
    }
  }, [editingMessageId, snapshots]);

  return <div className="gallery-manage">
    <div className="page-heading">
      <div><span className="kicker">SNAPSHOT STORAGE</span><h1>Manage snapshots</h1><p>Import, annotate, and remove the immutable experiment packages available in this gallery.</p></div>
      <button className="button button--primary button--large" type="button" onClick={onImport} disabled={importing}>
        <Upload size={16} /> {importing ? "Importing…" : "Import new snapshot"}
      </button>
    </div>
    <div className="gallery-storage-location">
      <FolderArchive size={16} />
      <span>Mounted storage</span>
      <code>{storagePath ?? "Resolving roboscientist.toml…"}</code>
    </div>
    {loading ? <div className="page-loading"><span /> Reading mounted snapshots…</div> : snapshots.length ? <div className="gallery-table-wrap">
      <table className="gallery-table">
        <thead><tr><th>Snapshot</th><th>Message</th><th>Exported</th><th>Source</th><th>Content</th><th aria-label="Actions" /></tr></thead>
        <tbody>{snapshots.map((snapshot) => {
          const deleting = deletingSnapshotId === snapshot.snapshot.id;
          const message = messages[snapshot.snapshot.id] ?? "";
          const editing = editingMessageId === snapshot.snapshot.id;
          const saving = savingMessageSnapshotId === snapshot.snapshot.id;
          return <tr key={snapshot.snapshot.id}>
            <td><strong>{snapshot.snapshot.title}</strong><code>{snapshot.snapshot.id}</code></td>
            <td className="gallery-table__message">{editing ? <div className="gallery-message-editor">
              <textarea
                autoFocus
                value={messageDraft}
                maxLength={MAX_GALLERY_MESSAGE_LENGTH}
                rows={3}
                aria-label={`Message for ${snapshot.snapshot.title}`}
                onChange={(event) => setMessageDraft(event.currentTarget.value)}
                disabled={saving}
              />
              <div><small>{messageDraft.length} / {MAX_GALLERY_MESSAGE_LENGTH}</small><span>
                <button className="button" type="button" disabled={saving} onClick={() => { setEditingMessageId(null); setMessageDraft(""); }}><X size={13} /> Cancel</button>
                <button className="button button--primary" type="button" disabled={saving} onClick={() => {
                  void onUpdateMessage(snapshot.snapshot.id, messageDraft).then((saved) => {
                    if (saved) { setEditingMessageId(null); setMessageDraft(""); }
                  });
                }}><Save size={13} /> {saving ? "Saving…" : "Save"}</button>
              </span></div>
            </div> : <div className="gallery-message-display">
              <p className={message ? "" : "is-empty"}>{message || "No message"}</p>
              <button className="gallery-message-edit" type="button" disabled={savingMessageSnapshotId !== null} onClick={() => { setEditingMessageId(snapshot.snapshot.id); setMessageDraft(message); }}><Pencil size={12} /> Edit</button>
            </div>}</td>
            <td><time dateTime={snapshot.snapshot.exportedAt}>{new Date(snapshot.snapshot.exportedAt).toLocaleString("en-US")}</time></td>
            <td><strong>{snapshot.snapshot.sourceProject.name}</strong><small>{englishGalleryText(snapshot.snapshot.sourceProject.id)}</small></td>
            <td><span>{snapshot.lineage.nodes.length} evolution {snapshot.lineage.nodes.length === 1 ? "node" : "nodes"}</span><span>{snapshot.experiments.length} {snapshot.experiments.length === 1 ? "experiment" : "experiments"}</span><span>{snapshot.runs.length} {snapshot.runs.length === 1 ? "run" : "runs"}</span><span>{snapshot.reports.length} {snapshot.reports.length === 1 ? "report" : "reports"}</span></td>
            <td><div className="gallery-table__actions">
              <button className="button" type="button" onClick={() => onOpen(snapshot.snapshot.id)}><Eye size={14} /> Open</button>
              <button className="button gallery-delete-button" type="button" disabled={deleting} onClick={() => onDelete(snapshot)}><Trash2 size={14} /> {deleting ? "Deleting…" : "Delete"}</button>
            </div></td>
          </tr>;
        })}</tbody>
      </table>
    </div> : <section className="gallery-manage-empty"><PackageOpen size={24} /><strong>No snapshots in mounted storage</strong><p>Use “Import new snapshot” above to add the first package.</p></section>}
  </div>;
}

function GalleryReport({
  report,
  onSelectExperiment,
  onSelectRun
}: {
  report: ResearchReportDetail;
  onSelectExperiment: (experimentId: string) => void;
  onSelectRun: (runId: string) => void;
}) {
  const downloadHref = useMemo(
    () => URL.createObjectURL(new Blob([report.markdown], { type: "text/markdown;charset=utf-8" })),
    [report.markdown]
  );
  useEffect(() => () => URL.revokeObjectURL(downloadHref), [downloadHref]);
  return <ResearchReportReader
    report={report}
    embedded
    readOnly
    downloadHref={downloadHref}
    onSelectExperiment={onSelectExperiment}
    onSelectRun={onSelectRun}
  />;
}

function GalleryReportsView({
  snapshot,
  onSelectExperiment,
  onSelectRun
}: {
  snapshot: GallerySnapshot;
  onSelectExperiment: (experimentId: string) => void;
  onSelectRun: (runId: string) => void;
}) {
  const reports = useMemo(
    () => [...snapshot.reports].sort((left, right) => Date.parse(right.publishedAt) - Date.parse(left.publishedAt)),
    [snapshot.reports]
  );
  const [selectedReportId, setSelectedReportId] = useState<string | null>(reports[0]?.reportId ?? null);
  useEffect(() => {
    if (!selectedReportId || !reports.some((report) => report.reportId === selectedReportId)) {
      setSelectedReportId(reports[0]?.reportId ?? null);
    }
  }, [reports, selectedReportId]);
  const report = reports.find((entry) => entry.reportId === selectedReportId) ?? null;
  if (!reports.length) return <Panel title="Reports" eyebrow="SAVED SNAPSHOT">
    <EmptyState title="No reports in this snapshot" detail="Evolution and run evidence remain available in the other views." />
  </Panel>;
  return <div className="cockpit-report-view report-layout">
    <aside className="report-history" aria-label="Report history">
      <header><div><span className="panel__eyebrow">SAVED TOPIC</span><h2>Report history</h2></div><span>{reports.length}</span></header>
      <OverflowReveal as="p" className="report-history__thread" value={snapshot.snapshot.title} label="Snapshot title" />
      <nav aria-label="Reports in this snapshot">
        {reports.map((entry) => <button
          key={entry.reportId}
          type="button"
          className={entry.reportId === selectedReportId ? "selected" : ""}
          aria-current={entry.reportId === selectedReportId ? "page" : undefined}
          onClick={() => setSelectedReportId(entry.reportId)}
        >
          <span className={`report-history__kind report-history__kind--${entry.kind}`}><FileText size={13} /> {reportKindLabel(entry.kind)}</span>
          <OverflowReveal as="strong" value={entry.title} label="Report title" />
          <small>{entry.version ? `v${entry.version} · ` : ""}{formatReportTimestamp(entry.publishedAt)}</small>
        </button>)}
      </nav>
    </aside>
    {report ? <GalleryReport
      key={report.reportId}
      report={report}
      onSelectExperiment={onSelectExperiment}
      onSelectRun={onSelectRun}
    /> : null}
  </div>;
}

function GalleryPaperView({ snapshot, presentationContent }: { snapshot: GallerySnapshot; presentationContent?: ReactNode }) {
  const paper = [...snapshot.papers].sort((left, right) => right.version - left.version)[0] ?? null;
  const [assets, setAssets] = useState<{ pdf: string; source: string; buildLog: string } | null>(null);
  const [assetError, setAssetError] = useState<string | null>(null);
  useEffect(() => {
    let disposed = false;
    const urls: string[] = [];
    setAssets(null);
    setAssetError(null);
    if (!paper || presentationContent) return () => undefined;
    void Promise.all([
      gallerySnapshotAsset(snapshot.snapshot.id, paper.pdfPath),
      gallerySnapshotAsset(snapshot.snapshot.id, paper.sourceZipPath),
      gallerySnapshotAsset(snapshot.snapshot.id, paper.buildLogPath)
    ]).then(([pdf, source, buildLog]) => {
      if (!pdf || !source || !buildLog) throw new Error("This imported package is missing one or more paper assets.");
      urls.push(URL.createObjectURL(pdf), URL.createObjectURL(source), URL.createObjectURL(buildLog));
      if (!disposed) setAssets({ pdf: urls[0]!, source: urls[1]!, buildLog: urls[2]! });
    }).catch((error) => { if (!disposed) setAssetError(error instanceof Error ? error.message : String(error)); });
    return () => {
      disposed = true;
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, [paper?.paperId, snapshot.snapshot.id]);
  if (!paper) return <section className="gallery-paper-empty"><FileCheck2 size={30} /><h2>No paper in this snapshot</h2><p>Legacy snapshots and prepublication research remain fully viewable.</p></section>;
  return <section className="gallery-paper-view">
    <header><div><span>IMMUTABLE PAPER · V{paper.version}</span><h2>{paper.title}</h2><small>{paper.templateId} · {new Date(paper.publishedAt).toLocaleString("en-US")}</small></div>
      {assets ? <nav><a className="button" href={assets.pdf} download={paper.paperId + ".pdf"}><Download size={14} /> PDF</a><a className="button" href={assets.source} download={paper.paperId + "-source.zip"}><FileArchive size={14} /> Source</a><a className="button" href={assets.buildLog} download={paper.paperId + "-build.log"}><FileText size={14} /> Build log</a></nav> : null}
    </header>
    {presentationContent ?? (assetError ? <div className="degraded-banner"><FileCheck2 size={17} /><div><strong>Paper asset unavailable</strong><span>{assetError}</span></div></div> : assets ? <iframe title={paper.title} src={assets.pdf} /> : <div className="page-loading"><span /> Loading packaged paper…</div>)}
  </section>;
}

function GallerySnapshotPage({
  snapshot,
  onBack,
  presentation
}: {
  snapshot: GallerySnapshot;
  onBack: () => void;
  presentation?: GalleryPresentation;
}) {
  const [localView, setView] = useState<GalleryView>("evolution");
  const view = presentation?.view ?? localView;
  const [selectedExperimentId, setSelectedExperimentId] = useState<string | null>(snapshot.experiments[0]?.id ?? null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(() => pickTelemetryRun(snapshot.runs)?.id ?? snapshot.runs[0]?.id ?? null);
  const [runsAutoPlay, setRunsAutoPlay] = useState(true);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const summaryCanExpand = snapshot.snapshot.summary.length > 220
    || snapshot.snapshot.summary.split(/\r?\n/).length > 3;
  useEffect(() => {
    setView("evolution");
    setSelectedExperimentId(snapshot.experiments[0]?.id ?? null);
    setSelectedRunId(pickTelemetryRun(snapshot.runs)?.id ?? snapshot.runs[0]?.id ?? null);
    setSummaryExpanded(false);
    setRunsAutoPlay(true);
  }, [snapshot]);
  const openExperiment = (experimentId: string) => {
    setSelectedExperimentId(experimentId);
    setRunsAutoPlay(false);
    setView("runs");
  };
  const openRun = (runId: string) => {
    setSelectedRunId(runId);
    setRunsAutoPlay(false);
    setView("runs");
  };
  return <div className="gallery-snapshot-page">
    <div className="gallery-detail-back"><button type="button" onClick={onBack}><ArrowLeft size={14} /> Experiment Gallery</button></div>
    <div className="page-heading page-heading--detail gallery-detail-heading">
      <div><span className="kicker">Research snapshot</span><h1>{snapshot.snapshot.title}</h1>
        <div className={`gallery-detail-summary ${summaryExpanded ? "is-expanded" : ""}`}>
          <p id="gallery-detail-summary">{snapshot.snapshot.summary}</p>
          {summaryCanExpand ? <button type="button" aria-expanded={summaryExpanded} aria-controls="gallery-detail-summary" onClick={() => setSummaryExpanded((expanded) => !expanded)}>
            {summaryExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {summaryExpanded ? "Collapse summary" : "Show full summary"}
          </button> : null}
        </div>
      </div>
      <div className="gallery-detail-meta"><StatusPill tone="accent">Immutable</StatusPill><span><Clock3 size={13} /> {new Date(snapshot.snapshot.exportedAt).toLocaleString("en-US")}</span></div>
    </div>
    <section className="stat-grid gallery-stats" aria-label="Saved research snapshot">
      <article><span className="stat-icon stat-icon--blue"><Network size={17} /></span><div><strong>{snapshot.lineage.nodes.length}</strong><span>evolution nodes</span></div></article>
      <article><span className="stat-icon stat-icon--violet"><FlaskConical size={17} /></span><div><strong>{snapshot.experiments.length}</strong><span>experiments</span></div></article>
      <article><span className="stat-icon stat-icon--green"><BarChart3 size={17} /></span><div><strong>{snapshot.runs.length}</strong><span>recorded runs</span></div></article>
      <article><span className="stat-icon stat-icon--amber"><FileText size={17} /></span><div><strong>{snapshot.reports.length}</strong><span>published reports</span></div></article>
    </section>
    <div className="research-main gallery-research-main">
      <div className="cockpit-view-bar gallery-view-bar">
      <nav className="cockpit-view-tabs" aria-label="Saved research views">
        <button type="button" className={view === "requirement" ? "selected" : ""} onClick={() => setView("requirement")}><ShieldCheck size={14} /> Requirement <small>{snapshot.requirement ? `v${snapshot.requirement.revision}` : "—"}</small></button>
        <button type="button" className={view === "evolution" ? "selected" : ""} onClick={() => setView("evolution")}><Network size={14} /> Evolution <small>{snapshot.lineage.nodes.length}</small></button>
        <button type="button" className={view === "runs" ? "selected" : ""} onClick={() => { setRunsAutoPlay(true); setView("runs"); }}><BarChart3 size={14} /> Experiments &amp; Runs <small>{snapshot.experiments.length} · {snapshot.runs.length}</small></button>
        {/* The Deploy scene belongs only to archived film compositions. */}
        {presentation?.view === "deploy" ? <button type="button" className="selected"><Bot size={14} /> Deploy</button> : null}
        <button type="button" className={view === "reports" ? "selected" : ""} onClick={() => setView("reports")}><FileText size={14} /> Reports <small>{snapshot.reports.length}</small></button>
        <button type="button" className={view === "paper" ? "selected" : ""} onClick={() => setView("paper")}><FileCheck2 size={14} /> Paper <small>{snapshot.papers.length}</small></button>
      </nav>
    </div>
    <div key={view} className="gallery-view-stage">
    {view === "requirement" ? <ResearchRequirementView requirement={snapshot.requirement} source="gallery" /> : null}
    {view === "evolution" ? <GalleryEvolutionReplay key={snapshot.snapshot.id} snapshot={snapshot} presentationProgress={presentation?.progress} /> : null}
    {view === "runs" ? <GalleryRunsView
      autoPlay={runsAutoPlay}
      presentation={presentation ? { progress: presentation.progress ?? 0, scope: "focus" } : undefined}
      snapshot={snapshot}
      selectedExperimentId={selectedExperimentId}
      selectedRunId={selectedRunId}
      onSelectExperiment={setSelectedExperimentId}
      onSelectRun={setSelectedRunId}
    /> : null}
    {presentation?.view === "deploy" ? <GalleryDeployView /> : null}
    {view === "reports" ? <GalleryReportsView
      snapshot={snapshot}
      onSelectExperiment={openExperiment}
      onSelectRun={openRun}
    /> : null}
    {view === "paper" ? <GalleryPaperView snapshot={snapshot} presentationContent={presentation?.paperContent} /> : null}
    </div>
    </div>
  </div>;
}

export type GalleryPresentation = {
  snapshots: GallerySnapshot[];
  route: GalleryRoute;
  view?: GalleryView | "deploy";
  progress?: number;
  time?: number;
  paperContent?: ReactNode;
};

export function GalleryApp({ presentation }: { presentation?: GalleryPresentation } = {}) {
  useThemeController();
  const resolvedTheme = useCockpitStore((state) => state.resolvedTheme);
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (presentation || !headerRef.current) return;
    return enhanceNavigationGlass(headerRef.current);
  }, [presentation]);
  const setTheme = useCockpitStore((state) => state.setTheme);
  const [localRoute, setRoute] = useState<GalleryRoute>(parseGalleryRoute);
  const route = presentation?.route ?? localRoute;
  const routeKey = galleryRouteHash(route);
  useLayoutEffect(() => {
    if (!presentation) window.scrollTo({ top: 0, behavior: "instant" });
  }, [routeKey, presentation]);
  const [storedSnapshots, setSnapshots] = useState<GallerySnapshot[]>([]);
  const [publishedStudies, setPublishedStudies] = useState<GalleryStudy[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const snapshots = useMemo(
    () => galleryDisplaySnapshots(presentation?.snapshots ?? storedSnapshots),
    [presentation?.snapshots, storedSnapshots]
  );
  const studies = useMemo(
    () => publicReadOnly && !presentation ? publishedStudies : snapshots.map(studyFromSnapshot),
    [presentation, publishedStudies, snapshots]
  );
  const [messages, setMessages] = useState<Record<string, string>>({});
  const [storagePath, setStoragePath] = useState<string | null>(null);
  const [loading, setLoading] = useState(!presentation);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMessage, setImportMessage] = useState("");
  const [importDialogError, setImportDialogError] = useState<string | null>(null);
  const [deletingSnapshotId, setDeletingSnapshotId] = useState<string | null>(null);
  const [savingMessageSnapshotId, setSavingMessageSnapshotId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const updateRoute = () => {
      if (publicReadOnly && window.location.hash === "#/manage") {
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#/`);
      }
      setRoute(parseGalleryRoute());
    };
    updateRoute();
    window.addEventListener("hashchange", updateRoute);
    return () => window.removeEventListener("hashchange", updateRoute);
  }, []);
  useEffect(() => {
    if (route.page === "manage") return;
    setImportDialogOpen(false);
    setImportFile(null);
    setImportMessage("");
    setImportDialogError(null);
    setDragging(false);
  }, [route.page]);
  useEffect(() => {
    if (presentation) return;
    if (publicReadOnly) {
      void listPublishedStudies()
        .then(setPublishedStudies)
        .catch((storageError) => setError({
          title: "Published studies unavailable",
          message: storageError instanceof Error ? storageError.message : String(storageError)
        }))
        .finally(() => setLoading(false));
      return;
    }
    void listGallerySnapshots()
      .then((index) => {
        setSnapshots(index.snapshots);
        setMessages(index.messages);
        setStoragePath(index.storagePath);
      })
      .catch((storageError) => setError({
        title: "Snapshot storage unavailable",
        message: storageError instanceof Error ? storageError.message : String(storageError)
      }))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (!publicReadOnly || presentation || route.page !== "snapshot" || loading
      || snapshots.some((snapshot) => snapshot.snapshot.id === route.snapshotId)) return;
    const study = publishedStudies.find((entry) => entry.snapshot.id === route.snapshotId);
    if (!study) return;
    let cancelled = false;
    setDetailLoading(true);
    void loadPublishedSnapshot(study)
      .then((snapshot) => { if (!cancelled) setSnapshots((current) => [...current, snapshot]); })
      .catch((loadError) => { if (!cancelled) setError({
        title: "Study unavailable",
        message: loadError instanceof Error ? loadError.message : String(loadError)
      }); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [presentation, route, loading, publishedStudies, snapshots]);

  const navigate = (nextRoute: GalleryRoute) => {
    const hash = galleryRouteHash(nextRoute);
    if (window.location.hash === hash) setRoute(nextRoute);
    else window.location.hash = hash;
  };
  const refreshSnapshots = async () => {
    const index = await listGallerySnapshots();
    setSnapshots(index.snapshots);
    setMessages(index.messages);
    setStoragePath(index.storagePath);
  };
  const openImportDialog = (file: File | null = null) => {
    setImportFile(file);
    setImportMessage("");
    setImportDialogError(null);
    setImportDialogOpen(true);
  };
  const closeImportDialog = () => {
    if (importing) return;
    setImportDialogOpen(false);
    setImportFile(null);
    setImportMessage("");
    setImportDialogError(null);
  };
  const importSnapshot = async () => {
    if (!importFile) return;
    setImporting(true);
    setError(null);
    setNotice(null);
    try {
      await storeGallerySnapshot(importFile, importMessage);
      await refreshSnapshots();
      setNotice(`Imported “${importFile.name}” into mounted storage.`);
      setImportDialogOpen(false);
      setImportFile(null);
      setImportMessage("");
      setImportDialogError(null);
    } catch (importError) {
      const message = importError instanceof Error ? importError.message : String(importError);
      setImportDialogError(message);
      setError({
        title: "Import failed",
        message
      });
    } finally {
      setImporting(false);
    }
  };
  const saveSnapshotMessage = async (snapshotId: string, message: string): Promise<boolean> => {
    setSavingMessageSnapshotId(snapshotId);
    setError(null);
    setNotice(null);
    try {
      const updated = await updateGallerySnapshotMessage(snapshotId, message);
      setMessages((current) => ({ ...current, [snapshotId]: updated }));
      setNotice(updated ? "Snapshot message updated." : "Snapshot message cleared.");
      return true;
    } catch (updateError) {
      setError({
        title: "Message update failed",
        message: updateError instanceof Error ? updateError.message : String(updateError)
      });
      return false;
    } finally {
      setSavingMessageSnapshotId(null);
    }
  };
  const removeSnapshot = async (snapshot: GallerySnapshot) => {
    if (!window.confirm(`Delete “${snapshot.snapshot.title}” from mounted snapshot storage?`)) return;
    setDeletingSnapshotId(snapshot.snapshot.id);
    setError(null);
    setNotice(null);
    try {
      await deleteGallerySnapshot(snapshot.snapshot.id);
      await refreshSnapshots();
      setNotice(`Deleted “${snapshot.snapshot.title}”.`);
    } catch (deleteError) {
      setError({
        title: "Delete failed",
        message: deleteError instanceof Error ? deleteError.message : String(deleteError)
      });
    } finally {
      setDeletingSnapshotId(null);
    }
  };
  const dropFiles = (event: DragEvent<HTMLDivElement>) => {
    if (route.page !== "manage") return;
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0] ?? null;
    if (file) openImportDialog(file);
  };
  const selected = route.page === "snapshot"
    ? snapshots.find((snapshot) => snapshot.snapshot.id === route.snapshotId) ?? null
    : null;

  return <div
    className={`app-shell gallery-shell ${dragging ? "is-dragging" : ""}`}
    onDragEnter={(event) => {
      if (route.page === "manage") {
        event.preventDefault();
        setDragging(true);
      }
    }}
    onDragOver={(event) => { if (route.page === "manage") event.preventDefault(); }}
    onDragLeave={(event) => {
      if (route.page === "manage" && !event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
    }}
    onDrop={dropFiles}
  >
    <header ref={headerRef} className="gallery-header">
      <div className="gallery-header__inner">
        <div className="gallery-header__brand">
          <button className="gallery-wordmark" type="button" aria-label="RoboScientist Experiment Gallery" onClick={() => navigate({ page: "gallery" })}>RoboScientist</button>
        </div>
        <nav aria-label="Gallery navigation">
          {presentation ? null : <a className="gallery-backbar" href="/home/">← Simate · Home</a>}
          <button type="button" aria-label="Gallery" aria-current={route.page !== "manage" ? "page" : undefined} onClick={() => navigate({ page: "gallery" })}>Gallery</button>
          {!publicReadOnly ? <button type="button" aria-label="Manage" aria-current={route.page === "manage" ? "page" : undefined} onClick={() => navigate({ page: "manage" })}>Manage</button> : null}
        </nav>
        <button className="gallery-theme-toggle" type="button" title={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} theme`} aria-label={resolvedTheme === "dark" ? "Light theme" : "Dark theme"} onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}>{resolvedTheme === "dark" ? <Sun size={18} /> : <Moon size={18} />}</button>
      </div>
    </header>
    <main className="workspace">
      <div className="page-frame gallery-page-frame">
        {error ? <div className="degraded-banner gallery-error" role="alert"><PackageOpen size={17} /><div><strong>{error.title}</strong><span>{error.message}</span></div><button type="button" onClick={() => setError(null)}>Dismiss</button></div> : null}
        {notice ? <div className="gallery-notice" role="status"><FileCheck2 size={17} /><span>{notice}</span><button type="button" onClick={() => setNotice(null)}>Dismiss</button></div> : null}
        {route.page === "manage" && !publicReadOnly ? <GalleryManage
          snapshots={snapshots}
          messages={messages}
          loading={loading}
          importing={importing}
          deletingSnapshotId={deletingSnapshotId}
          savingMessageSnapshotId={savingMessageSnapshotId}
          storagePath={storagePath}
          onImport={() => openImportDialog()}
          onOpen={(snapshotId) => navigate({ page: "snapshot", snapshotId })}
          onDelete={(snapshot) => void removeSnapshot(snapshot)}
          onUpdateMessage={saveSnapshotMessage}
        /> : route.page === "snapshot" ? selected
          ? <GallerySnapshotPage snapshot={selected} onBack={() => navigate({ page: "gallery" })} presentation={presentation} />
          : loading || detailLoading
            ? <div className="page-loading"><span /> Reading mounted snapshot…</div>
            : <section className="gallery-empty-state"><PackageOpen size={28} /><strong>Snapshot not found</strong><p>It may have been removed from mounted storage.</p><button className="button" type="button" onClick={() => navigate({ page: "gallery" })}>Back to Gallery</button></section>
          : <GalleryHome
            studies={studies}
            presentationTime={presentation?.time}
            loading={loading}
            onManage={publicReadOnly ? undefined : () => navigate({ page: "manage" })}
            onOpen={(snapshotId) => navigate({ page: "snapshot", snapshotId })}
          />}
      </div>
    </main>
    {presentation ? null : <GalleryPublicFooter />}
    {route.page === "manage" && dragging ? <div className="gallery-drop-overlay"><Upload size={34} /><strong>Drop experiment package to import</strong></div> : null}
    <GalleryImportDialog
      open={route.page === "manage" && importDialogOpen}
      file={importFile}
      message={importMessage}
      importError={importDialogError}
      importing={importing}
      onFileChange={(file) => { setImportFile(file); setImportDialogError(null); }}
      onMessageChange={(message) => { setImportMessage(message); setImportDialogError(null); }}
      onClose={closeImportDialog}
      onImport={() => void importSnapshot()}
    />
  </div>;
}
